import {
  useAllPiiBackfillStates,
  usePauseShopsPiiBackfill,
  useStartShopsPiiBackfill,
} from "@/api/pii-vault/use-pii-backfill";
import {
  BackfillScopeFields,
  DEFAULT_SCOPE,
} from "@/components/pii-backfill/backfill-scope";
import {
  MAX_SHOPS_PER_START,
  ShopIdsInput,
} from "@/components/pii-backfill/shop-ids-input";
import { Badge } from "@babackapp/ui/components/badge";
import { Button } from "@babackapp/ui/components/button";
import { Pause, Play } from "lucide-react";
import { useMemo, useState } from "react";
import type { BackfillShop } from "./pii-backfill-shop-row";
import { PiiVerifyRow } from "./pii-verify-row";
import {
  VERIFY_STATUSES,
  type VerifyStatus,
  summarizeVerify,
} from "./pii-verify-status";

/**
 * Super-admin launcher of the verify run: checks, document by document, that
 * the vault returns exactly what the document holds. Nothing is written. A
 * backfill is trusted once every shop is clean.
 */
export function PiiVerifyTab({ shops }: { shops: BackfillShop[] }) {
  const overview = useAllPiiBackfillStates();
  const start = useStartShopsPiiBackfill();
  const pause = usePauseShopsPiiBackfill();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [scope, setScope] = useState(DEFAULT_SCOPE);

  const rows = useMemo(() => {
    const states = overview.data?.states ?? [];
    return (
      [...shops]
        .map((shop) => ({
          shop,
          summary: summarizeVerify(states.filter((s) => s.shopId === shop.id)),
        }))
        // Shops with mismatches first, then the lightest ones.
        .sort(
          (a, b) =>
            b.summary.mismatched - a.summary.mismatched ||
            a.shop.totalOrders - b.shop.totalOrders,
        )
    );
  }, [shops, overview.data]);

  const counts = rows.reduce<Partial<Record<VerifyStatus, number>>>(
    (acc, { summary }) => ({
      ...acc,
      [summary.status]: (acc[summary.status] ?? 0) + 1,
    }),
    {},
  );
  const shopIds = [...selected];
  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const selectPending = () =>
    setSelected(
      new Set(
        rows
          .filter(({ summary }) => summary.status === "pending")
          .slice(0, MAX_SHOPS_PER_START)
          .map(({ shop }) => shop.id),
      ),
    );

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground max-w-3xl">
        Vérifie, document par document, que le coffre rend exactement ce que le
        document porte en clair : contact et adresses, champ par champ. Rien
        n'est écrit. Un shop est conforme quand la vérification se termine sans
        écart. Les écarts sont détaillés dans Datadog, événement{" "}
        <code>pii_vault.verify.mismatch</code>, avec le document et les champs
        concernés. À terminer sur tous les shops avant d'ouvrir la lecture.
      </p>
      <div className="flex flex-wrap gap-2">
        {(Object.keys(VERIFY_STATUSES) as VerifyStatus[]).map((status) => (
          <Badge key={status} variant="outline">
            {VERIFY_STATUSES[status]} : {counts[status] ?? 0}
          </Badge>
        ))}
      </div>
      <ShopIdsInput
        knownIds={new Set(shops.map((shop) => shop.id))}
        onSelect={(ids) => setSelected(new Set(ids))}
      />
      <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 p-3">
        <Button size="sm" variant="outline" onClick={selectPending}>
          Sélectionner les shops à vérifier (max {MAX_SHOPS_PER_START})
        </Button>
        <BackfillScopeFields
          value={scope}
          onChange={setScope}
          hiddenKeys={["isRestart"]}
        />
        <Button
          size="sm"
          disabled={shopIds.length === 0 || start.isPending}
          onClick={() =>
            start.mutate({
              body: {
                shopIds,
                mode: "verify",
                isParcelsOnly: scope.isParcelsOnly,
              },
            })
          }
        >
          <Play className="w-4 h-4" />
          Vérifier {shopIds.length} shop(s)
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={shopIds.length === 0 || pause.isPending}
          onClick={() => pause.mutate({ body: { shopIds } })}
        >
          <Pause className="w-4 h-4" />
          Pause
        </Button>
      </div>
      <div className="overflow-x-auto rounded-lg border bg-background">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-2" />
              <th className="px-3 py-2">Shop</th>
              <th className="px-3 py-2 text-right">Commandes</th>
              <th className="px-3 py-2 text-right">Écarts</th>
              <th className="px-3 py-2">Vérification</th>
              <th className="px-3 py-2">Progression</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ shop, summary }) => (
              <PiiVerifyRow
                key={shop.id}
                shop={shop}
                summary={summary}
                isSelected={selected.has(shop.id)}
                onToggle={() => toggle(shop.id)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
